import React, { useId, useState } from "react";
import Folder from "../Icons/EditMode/Folder";

export interface GenericFileInputButtonProps {
  title?: string;
  accept?: string;
  maxSizeMb?: number;
  required?: boolean;
  onFileSelected: (file: File) => void;
  onFileRemoved: () => void;
  hint?: string;
  validate?: (file: File) => string | null;
  buttonLabel?: string;
  variant?: "outline" | "accent";
}

const GenericFileInputButton: React.FC<GenericFileInputButtonProps> = ({
  title,
  accept,
  maxSizeMb = 25,
  required,
  onFileSelected,
  onFileRemoved,
  hint,
  validate,
  buttonLabel = "CHOOSE FILE",
  variant = "outline",
}) => {
  const id = useId().replace(/:/g, "");
  const inputId = `generic-file-input-${id}`;
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      setSelectedFile(null);
      setError(null);
      onFileRemoved();
      return;
    }
    const sizeMb = file.size / (1024 * 1024);
    if (sizeMb > maxSizeMb) {
      setError(`File size exceeds ${maxSizeMb} MB limit`);
      e.target.value = "";
      return;
    }
    const validationError = validate?.(file) ?? null;
    if (validationError) {
      setError(validationError);
      e.target.value = "";
      return;
    }
    setSelectedFile(file);
    setError(null);
    onFileSelected(file);
  };

  const handleClear = () => {
    const input = document.getElementById(inputId) as HTMLInputElement | null;
    if (input) {
      input.value = "";
    }
    setSelectedFile(null);
    setError(null);
    onFileRemoved();
  };

  return (
    <div className="flex flex-col gap-1.5 self-stretch">
      {title !== undefined && (
        <span
          className={`text-base leading-[1.125rem] ${
            required ? "text-[#BC3323]" : "text-neutral-dark"
          }`}
        >
          {title}
        </span>
      )}
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex flex-wrap items-center gap-2">
        <label
          className={
            variant === "accent"
              ? "flex flex-nowrap py-2 pr-2 pl-3 justify-center items-center gap-2 self-start md:w-[11.3125rem] lg:py-3 lg:pr-2 lg:pl-3 lg:w-[11.8125rem] text-white text-center font-decimal font-base font-medium leading-[1.125rem] md:text-[1.125rem] md:leading-5 cursor-pointer bg-Default border border-Default border-solid hover:bg-accent-Default-dark transition-colors uppercase whitespace-nowrap"
              : "flex flex-nowrap py-2 pr-2 pl-3 justify-center items-center gap-2 self-start border border-solid border-[#474F56] md:w-[11.3125rem] lg:py-3 lg:pr-2 lg:pl-3 lg:w-[11.8125rem] text-[#474F56] text-center font-decimal font-base font-medium leading-[1.125rem] md:text-[1.125rem] md:leading-5 cursor-pointer hover:bg-gray-50 whitespace-nowrap"
          }
          htmlFor={inputId}
        >
          <input
            type="file"
            id={inputId}
            className="pointer-events-none sr-only"
            onChange={handleChange}
            accept={accept}
            name={inputId}
          />
          <span className="font-medium font-decimal text-[1rem] leading-[1.125rem] lg:text-[1.125rem] lg:leading-5 whitespace-nowrap">
            {buttonLabel}
          </span>
          <div
            className={`w-5 h-5 lg:w-6 lg:h-6 ${variant === "accent" ? "fill-white" : "fill-[#474F56]"}`}
          >
            <Folder />
          </div>
        </label>
        {selectedFile && (
          <>
            <span className="text-sm text-neutral-dark truncate max-w-[12rem]" title={selectedFile.name}>
              {selectedFile.name}
            </span>
            <button
              type="button"
              onClick={handleClear}
              className="text-sm text-[#474F56] underline hover:no-underline"
            >
              Clear
            </button>
          </>
        )}
      </div>
      {hint && <p className="text-sm text-gray-500">{hint}</p>}
    </div>
  );
};

export default GenericFileInputButton;
